/**
 * TerraformLivreCard — cartão do AJUSTE LIVRE no stage (spec 2026-10-08 D11).
 *
 * O @DevOps escreveu arquivos inteiros do stage; o cartão mostra cada arquivo
 * com a justificativa e os avisos da análise. O CONTEÚDO de HCL nunca é
 * renderizado aqui: o diff legível vai no MR, e o efeito aparece pelo plan
 * (o bloco do pedido, o mesmo do `TerraformAdjustCard`).
 *
 * Padrão visual de `docs/DESIGN_SYSTEM.md`: Barlow no texto, JetBrains Mono em
 * nomes de arquivo e endereços, alinhamento à esquerda, e o mesmo ícone SVG
 * do cartão de ajuste — nenhum ícone novo.
 */
import { Badge, Card, Group, Stack, Text } from '@mantine/core'
import { IconAdjustments } from '@tabler/icons-react'
import type { PedidoDoCartao } from '@/api/terraformRunner'
import { Dado } from './Dado'
import { BlocoDoPedido, type TerraformAdjustCardStatus } from './TerraformAdjustCard'

export interface ArquivoDoLivre {
  arquivo: string
  justificativa: string
}

export interface TerraformLivreCardProps {
  stageChave: string
  pedidoTexto: string
  arquivos: ArquivoDoLivre[]
  avisos: string[]
  status?: TerraformAdjustCardStatus
  canConfirmPermission?: boolean
  pedido?: PedidoDoCartao | null
  pedidoCarregando?: boolean
  pedidoIndisponivel?: boolean
  motivoBloqueio?: string | null
  onConfirm?: () => void
  isConfirming?: boolean
  onDecline?: () => void
  isDeclining?: boolean
  /** O "Quero seguir" do livre guardado (spec 2026-10-15 D3). */
  onRetomar?: () => void
  isRetomando?: boolean
  /** `params.aviso_fila` (spec 2026-10-15 §5.2): nasceu na fila; sem Confirmar antes da vez. */
  avisoFila?: string | null
}

const BARLOW = { fontFamily: '"Barlow", Arial, sans-serif' }
const MONO = { fontFamily: '"JetBrains Mono", monospace' }

export function TerraformLivreCard({
  stageChave,
  pedidoTexto,
  arquivos,
  avisos,
  status = 'pending',
  canConfirmPermission = true,
  pedido,
  pedidoCarregando = false,
  pedidoIndisponivel = false,
  motivoBloqueio,
  onConfirm,
  isConfirming = false,
  onDecline,
  isDeclining = false,
  onRetomar,
  isRetomando = false,
  avisoFila = null,
}: TerraformLivreCardProps) {
  const isPending = status === 'pending'
  // Mesmo gate do cartão de ajuste: com pedido, o ESTADO dele decide; sem
  // leitura do pedido, fail-closed.
  const gatePendente = pedido ? pedido.pode_confirmar : !pedidoIndisponivel && isPending && !avisoFila
  const podeConfirmar = gatePendente && canConfirmPermission

  return (
    <Card withBorder padding="sm" radius="md" style={{ maxWidth: 480 }}>
      <Group gap="xs" mb="xs" wrap="nowrap" align="flex-start">
        <IconAdjustments size={16} style={{ marginTop: 2, flexShrink: 0 }} />
        <Stack gap={2} style={{ flex: 1 }}>
          <Text size="lg" fw={600} style={BARLOW}>
            Ajuste livre em <Dado>{stageChave}</Dado>
          </Text>
          <Text size="sm" style={BARLOW}>{pedidoTexto}</Text>
        </Stack>
      </Group>

      <Stack gap={6} mb="xs">
        {arquivos.map((a) => (
          <Stack key={a.arquivo} gap={0}>
            <Text size="sm" style={MONO}>{a.arquivo}</Text>
            <Text size="xs" c="dimmed" style={BARLOW}>{a.justificativa}</Text>
          </Stack>
        ))}
      </Stack>

      {avisos.length > 0 && (
        <Stack gap={2} mb="xs">
          {avisos.map((aviso) => (
            <Group key={aviso} gap={6} wrap="nowrap" align="flex-start">
              <Badge size="xs" color="yellow" variant="light" style={{ flexShrink: 0 }}>
                Atenção
              </Badge>
              <Text size="sm" style={BARLOW}>{aviso}</Text>
            </Group>
          ))}
        </Stack>
      )}

      <Text size="xs" c="dimmed" mb="xs" style={BARLOW}>
        O plan roda em seguida; a aprovação é de um DevOps Sênior.
      </Text>

      <BlocoDoPedido
        pedido={pedido}
        pedidoCarregando={pedidoCarregando}
        pedidoIndisponivel={pedidoIndisponivel}
        motivoBloqueio={motivoBloqueio}
        isPending={isPending}
        podeConfirmar={podeConfirmar}
        onConfirm={onConfirm}
        isConfirming={isConfirming}
        onDecline={onDecline}
        isDeclining={isDeclining}
        onRetomar={onRetomar}
        avisoFila={avisoFila}
        isRetomando={isRetomando}
      />
    </Card>
  )
}
